import type { StateCreator } from 'zustand';
import type Notification from '../../interfaces/Notification';

export interface NotificationsSlice {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Notification) => void;
  updateNotification: (id: string, updates: Partial<Notification>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
  removeNotification: (id: string) => void;
  _maxNotifications: number;
}

type WithMW = []; // change to [['zustand/devtools', never], ['zustand/subscribeWithSelector', never]] if necessary

export const createNotificationsSlice: StateCreator<
  NotificationsSlice,
  WithMW,
  [],
  NotificationsSlice
> = (set) => ({
  notifications: [],
  unreadCount: 0,
  _maxNotifications: 100,

  /**
   * Adds a new notification to the list.
   * If the list exceeds the maximum limit, removes the oldest notifications (FIFO).
   *
   * @param notification - The notification to add
   */
  addNotification: (notification: Notification) => {
    set((state) => {
      const currentNotifications = Array.isArray(state.notifications) ? state.notifications : [];
      const newNotifications = [...currentNotifications, notification];

      // Remove oldest notifications if we exceed the limit
      const trimmedNotifications =
        newNotifications.length > state._maxNotifications
          ? newNotifications.slice(-state._maxNotifications)
          : newNotifications;

      const unreadCount = trimmedNotifications.filter((n) => !n.isRead).length;

      return {
        ...state,
        notifications: trimmedNotifications,
        unreadCount,
      };
    });
  },

  /**
   * Updates an existing notification with the provided updates.
   *
   * @param id - The ID of the notification to update
   * @param updates - The partial updates to apply
   */
  updateNotification: (id: string, updates: Partial<Notification>) => {
    set((state) => {
      const currentNotifications = Array.isArray(state.notifications) ? state.notifications : [];
      const updatedNotifications = currentNotifications.map((notification) =>
        notification.id === id ? { ...notification, ...updates } : notification,
      );

      const unreadCount = updatedNotifications.filter((n) => !n.isRead).length;

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount,
      };
    });
  },

  /**
   * Marks a specific notification as read with the current timestamp.
   *
   * @param id - The ID of the notification to mark as read
   */
  markAsRead: (id: string) => {
    set((state) => {
      const currentNotifications = Array.isArray(state.notifications) ? state.notifications : [];
      const updatedNotifications = currentNotifications.map((notification) =>
        notification.id === id && !notification.isRead
          ? {
              ...notification,
              isRead: true,
              readAt: new Date().toISOString(),
            }
          : notification,
      );

      const unreadCount = updatedNotifications.filter((n) => !n.isRead).length;

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount,
      };
    });
  },

  /**
   * Marks all notifications as read with the current timestamp.
   */
  markAllAsRead: () => {
    set((state) => {
      const currentNotifications = Array.isArray(state.notifications) ? state.notifications : [];
      const now = new Date().toISOString();
      const updatedNotifications = currentNotifications.map((notification) =>
        !notification.isRead ? { ...notification, isRead: true, readAt: now } : notification,
      );

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: 0,
      };
    });
  },

  /**
   * Removes all notifications from the list.
   */
  clearAll: () => {
    set((state) => ({
      ...state,
      notifications: [],
      unreadCount: 0,
    }));
  },

  /**
   * Removes a specific notification from the list.
   *
   * @param id - The ID of the notification to remove
   */
  removeNotification: (id: string) => {
    set((state) => {
      const currentNotifications = Array.isArray(state.notifications) ? state.notifications : [];
      const updatedNotifications = currentNotifications.filter(
        (notification) => notification.id !== id,
      );

      const unreadCount = updatedNotifications.filter((n) => !n.isRead).length;

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount,
      };
    });
  },
});
